import { useCallback, useEffect, useState } from 'react';
import {
    BadResetLinkError,
    ResetOutcome,
    saveNewPassword,
    verifyResetLink,
} from '../../api/passwordResetApi';

export type ResetPasswordState =
    | { step: 'checking' }
    | { step: 'badLink' }
    | { step: 'loadError'; message: string }
    | { step: 'form'; email: string; saving: boolean; error: string | null }
    | { step: 'done'; email: string; outcome: ResetOutcome };

/** Checks the link once, then saves the new password and signs in with it */
export const useResetPassword = (oobCode: string | null) => {
    const [state, setState] = useState<ResetPasswordState>({ step: 'checking' });

    useEffect(() => {
        if (!oobCode) {
            setState({ step: 'badLink' });
            return;
        }
        let cancelled = false;
        verifyResetLink(oobCode)
            .then((email) => {
                if (!cancelled) setState({ step: 'form', email, saving: false, error: null });
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                setState(err instanceof BadResetLinkError
                    ? { step: 'badLink' }
                    : { step: 'loadError', message: err instanceof Error ? err.message : String(err) });
            });
        return () => {
            cancelled = true;
        };
    }, [oobCode]);

    const save = useCallback(async (newPassword: string) => {
        if (!oobCode || state.step !== 'form' || state.saving) return;
        const { email } = state;
        setState({ step: 'form', email, saving: true, error: null });
        try {
            const outcome = await saveNewPassword(oobCode, email, newPassword);
            setState({ step: 'done', email, outcome });
        } catch (err) {
            setState(err instanceof BadResetLinkError
                ? { step: 'badLink' }
                : { step: 'form', email, saving: false, error: err instanceof Error ? err.message : String(err) });
        }
    }, [oobCode, state]);

    return { state, save };
};
